// Company-wide non-working days on the client (round 22).
//
// Ported from Zanetto Tools' Schedule.jsx (makeNonWorking / nwShade /
// makeWorksOn / daySegments) — reimplemented, with its recorded reasoning
// carried across as prose. Everything here is pure: SchedulePage builds the
// lookups once per data load and hands them down, so the views stay
// data-dumb.
import { addDays, eachDate, type DateOnly } from './dates';
import type { NonWorkingDay } from '../schedulingApi';

export interface NwInfo {
  nonWorking: true;
  /** True for a named entry (holiday/RDO/shutdown); false for a weekday. */
  dated: boolean;
  name: string | null;
  kind: NonWorkingDay['kind'] | 'weekend';
}

export type NwLookup = (date: DateOnly) => NwInfo | null;

/**
 * Build the per-date lookup. A dated entry beats the recurring weekday —
 * "Christmas Day" on the board says more than "weekend" does, and the manage
 * page lets a dated entry land on any day of the week.
 */
export function makeNwInfo(weekdays: number[], days: NonWorkingDay[]): NwLookup {
  const off = new Set(weekdays);
  const dated = new Map<string, NonWorkingDay>();
  for (const day of days) {
    for (const date of eachDate(day.startDate, day.endDate)) {
      // First entry wins on an overlap — stable, and overlapping entries are
      // a data-entry oddity, not a case worth a rule.
      if (!dated.has(date)) dated.set(date, day);
    }
  }

  return (date: DateOnly): NwInfo | null => {
    const named = dated.get(date);
    if (named) return { nonWorking: true, dated: true, name: named.name, kind: named.kind };
    // JS getDay() numbering end to end (migration 021) — parse as local
    // midnight so the weekday is the tenant's, not UTC's.
    const dow = new Date(`${date}T00:00:00`).getDay();
    if (off.has(dow)) return { nonWorking: true, dated: false, name: null, kind: 'weekend' };
    return null;
  };
}

/**
 * The one tint function every view reads, so two views cannot disagree about
 * what a shut day looks like.
 *
 * Amber for dated days, NOT ZT's orange — Dayboard's "today" is already a
 * light orange brand tint (`bg-brand-50`), and ZT's orange-50 holiday cells
 * would read as a second today. Amber is the product's existing "careful"
 * semantic, and a public holiday is exactly a heads-up. Weekends keep ZT's
 * grey weights. `strong` is for headers and date axes; cells take the softer
 * half. Today always wins — callers check that first.
 *
 * ONE STEP DEEPER THAN ZT'S SCALE, MEASURED, NOT TASTE (2026-08-31): the
 * obvious port (amber-50 cells) sits ΔE 5.7 from `brand-50` in CIELAB —
 * a holiday cell indistinguishable from the today tint, the exact collision
 * this hue swap exists to prevent. amber-100 is ΔE 18.8 from brand-50 and
 * amber-200 is 39.4 from brand-100, both beyond the ~14 the job palette
 * already tolerates internally. red-700 names measure 5.8:1 on amber-100
 * and 5.2:1 on amber-200 — AA either way. docs/phase-8-nonworking-colour.md
 * carries the numbers.
 */
export function nwShade(info: NwInfo | null, strong = false): string {
  if (!info) return '';
  if (info.dated) return strong ? 'bg-amber-200' : 'bg-amber-100';
  return strong ? 'bg-gray-300' : 'bg-gray-200';
}

/** What the board calls the day in a warning: the name, or plainly "weekend". */
export function nwLabel(info: NwInfo): string {
  return info.name ?? 'weekend';
}

interface Spannable {
  startDate: DateOnly;
  endDate: DateOnly;
}

export type WorksOn = (booking: Spannable, date: DateOnly) => boolean;

/**
 * Does this booking occupy this date, as far as the board is concerned?
 *
 * True when the booking spans the day AND either the day is worked or the
 * booking's ENTIRE span is non-working. That second clause is ZT's deliberate-
 * Saturday-job rule, kept on purpose: a booking placed wholly on a weekend was
 * placed there by someone who meant it, and a board that hides it would be
 * hiding real work. A booking that merely spans a weekend, though, gaps over
 * it — the crew is not on site on Sunday just because the job runs Friday to
 * Tuesday.
 */
export function makeWorksOn(nwInfo: NwLookup): WorksOn {
  // Memoised per booking span, not per (booking, date): the all-non-working
  // check walks the whole span and the grid asks about every visible day.
  const wholeSpanOff = new Map<string, boolean>();

  const spanIsAllOff = (booking: Spannable): boolean => {
    const key = `${booking.startDate}..${booking.endDate}`;
    const cached = wholeSpanOff.get(key);
    if (cached !== undefined) return cached;
    const result = eachDate(booking.startDate, booking.endDate).every((d) => nwInfo(d) !== null);
    wholeSpanOff.set(key, result);
    return result;
  };

  return (booking, date) => {
    if (date < booking.startDate || date > booking.endDate) return false;
    if (nwInfo(date) === null) return true;
    return spanIsAllOff(booking);
  };
}

/**
 * Split a booking into contiguous runs of days it actually occupies inside a
 * visible window, so a timeline bar visibly GAPS over the weekend instead of
 * running through it — the single most ZT-looking thing on the boards.
 */
export function daySegments(
  booking: Spannable,
  visStart: DateOnly,
  visEnd: DateOnly,
  worksOn: WorksOn
): { start: DateOnly; end: DateOnly }[] {
  const from = booking.startDate > visStart ? booking.startDate : visStart;
  const to = booking.endDate < visEnd ? booking.endDate : visEnd;
  if (from > to) return [];

  const segments: { start: DateOnly; end: DateOnly }[] = [];
  let current: { start: DateOnly; end: DateOnly } | null = null;

  for (let date = from; date <= to; date = addDays(date, 1)) {
    if (worksOn(booking, date)) {
      if (current) current.end = date;
      else {
        current = { start: date, end: date };
        segments.push(current);
      }
    } else {
      current = null;
    }
  }
  return segments;
}
